import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, IconButton,Grid2 } from "@mui/material";
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';

const VideoSection = ({ children, size = 1.05 }) => {
    const scrollRef = useRef(null);
    const videoRefs = useRef([]);
    const observerRef = useRef(null);
    const [activeIndex, setActiveIndex] = useState(0);

    const scroll = useCallback((direction) => {
        const container = scrollRef.current;
        if (container && container.children.length > 0) {
            const cardWidth = container.children[0].offsetWidth * size;
            container.scrollBy({
                left: direction === "left" ? -cardWidth : cardWidth,
                behavior: "smooth",
            });
        }
    }, [size]);

    const controlVideoPlayback = useCallback((active) => {
        videoRefs.current.forEach((iframe, idx) => {
            if (!iframe) return;
            iframe.contentWindow?.postMessage(
                JSON.stringify({
                    event: "command",
                    func: idx === active ? "playVideo" : "pauseVideo",
                    args: [],
                }),
                "*"
            );
        });
    }, []);

    useEffect(() => {
        if (!videoRefs.current.length) return;

        // Clear any previous observer
        if (observerRef.current) {
            observerRef.current.disconnect();
        }

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        const index = videoRefs.current.findIndex(ref => ref === entry.target);
                        if (index !== -1 && index !== activeIndex) {
                            setActiveIndex(index);
                            controlVideoPlayback(index);
                        }
                    }
                });
            },
            {
                root: scrollRef.current,
                threshold: 0.6, // Play video if 60% visible
            }
        );

        videoRefs.current.forEach(iframe => {
            if (iframe) observer.observe(iframe);
        });

        observerRef.current = observer;

        return () => observer.disconnect();
    }, [controlVideoPlayback, activeIndex]);

    useEffect(() => {
        // Auto-play first video on mount
        controlVideoPlayback(0);
    }, [controlVideoPlayback]);

    const totalPages = Math.ceil(React.Children.count(children) / size) || 1;

    return (
        <Box sx={{ position: "relative", "&:hover .arrow-button": { opacity: 1 } }}>
            <IconButton className="arrow-button" onClick={() => scroll("left")} sx={styles.leftArrow}>
                <NavigateBeforeIcon sx={{ color: "#fff" }} />
            </IconButton>
            <IconButton className="arrow-button" onClick={() => scroll("right")} sx={styles.rightArrow}>
                <NavigateNextIcon sx={{ color: "#fff" }} />
            </IconButton>

            <Grid2 container ref={scrollRef} sx={styles.scrollContainer}>
                {React.Children.map(children, (child, index) => (
                    <Box
                        key={index}
                        sx={{ minWidth: "100%" }}
                        ref={(el) => {
                            const iframe = el?.querySelector('iframe');
                            if (iframe) videoRefs.current[index] = iframe;
                        }}
                    >
                        {child}
                    </Box>
                ))}
            </Grid2>

            <Box sx={styles.dotsContainer}>
                {Array.from({ length: totalPages }).map((_, index) => (
                    <Box
                        key={index}
                        sx={{
                            ...styles.dot,
                            backgroundColor: activeIndex === index ? "#FF0000" : "#D9D9D9",
                        }}
                    />
                ))}
            </Box>
        </Box>
    );
};

const styles = {
    scrollContainer: {
        overflowX: "auto",
        display: "flex",
        flexWrap: "nowrap",
        gap: "30px",
        scrollbarWidth: "none",
        "&::-webkit-scrollbar": { display: "none" },
        scrollBehavior: "smooth",
    },
    leftArrow: {
        position: "absolute",
        top: "50%",
        left: 10,
        transform: "translateY(-50%)",
        bgcolor: "rgba(0, 0, 0, 0.2)",
        p: 2,
        borderRadius:2,
        zIndex: 2,
        opacity: 0,
        transition: "opacity 0.3s",
        "&:hover": {
            bgcolor: "rgba(0, 0, 0, 0.4)"
        }
    },
    rightArrow: {
        position: "absolute",
        top: "50%",
        right: 10,
        transform: "translateY(-50%)",
        bgcolor: "rgba(0, 0, 0, 0.2)",
        p: 2,
        borderRadius:2,
        zIndex: 2,
        opacity: 0,
        transition: "opacity 0.3s",
        "&:hover": {
            bgcolor: "rgba(0, 0, 0, 0.4)"
        }
    },
    dotsContainer: {
        display: "flex",
        justifyContent: "center",
        mt: 4,
    },
    dot: {
        width: 12,
        height: 12,
        borderRadius: "50%",
        margin: "0 3px",
        transition: "background-color 0.3s ease",
    },
};

export default VideoSection;
